import type { LineID } from '@metro/graph'

export type LegType = 'ride' | 'transfer' | 'walk'

export interface Stop {
  readonly stationId: string
  readonly arrival_offset_s: number
  /** Cluster identifier shared by sibling per-line records. Falls back
   *  to `stationId` in `signature(r)` when missing — used so the
   *  routing dedup signature treats two records of the same physical
   *  station (e.g. `central.940GZZLUSTD` and `jubilee.940GZZLUSTD` both
   *  carrying naptan `940GZZLUSTD`) as a single landing. */
  readonly naptanId?: string
  /** `true` when the train physically passes through this station
   *  without opening its doors (i.e. it isn't a revenue stop on the
   *  TFL-selected service pattern). Backend marks bridge intermediates
   *  inserted to complete the schema connection graph as passthroughs;
   *  canonical examples are Hanwell + Southall on the Elizabeth
   *  Heathrow Express fast branch and Mornington Crescent on Northern
   *  Charing-Cross variants.
   *
   *  Renderers must still draw the polyline through the station so the
   *  route doesn't visually break, but should NOT draw a marker /
   *  label / countdown chip for it. */
  readonly passthrough?: boolean
}

export interface Leg {
  readonly type: LegType
  readonly line_id?: LineID
  readonly stops?: readonly Stop[]
  readonly hint_carriage?: string
  readonly transition_id?: string
  readonly from_station_id?: string
  readonly to_station_id?: string
  readonly duration_s: number
  /**
   * Convenient-transfer car tokens (FIRST / NEAR_FIRST / CENTER /
   * NEAR_END / END / ALL), present on `transfer` legs whose transition
   * carries curated/default cars (backend wire, schema v1.23+). Online
   * (TFL) routes carry the backend-resolved set here; offline (local)
   * routes leave it unset and the RouteDetails consumer derives the
   * equivalent from the bundled schema's per-approach data. Either way
   * the cars belong to the PRECEDING ride leg's possibleTransfer.
   */
  readonly convenient_cars?: readonly string[]
}

export interface Route {
  id: string
  total_time_s: number
  transfer_count: number
  walking_time_s: number
  legs: Leg[]
}

/**
 * Returns the ordered list of "naptanId-or-stationId|lineId" tokens for
 * all ride-leg stops. Used as the canonical identity of a route for
 * dedup purposes.
 *
 * Each token prefers `stop.naptanId` over `stop.stationId` so two
 * routes that travel through the same physical station via distinct
 * per-line records (e.g. `central.940GZZLUSTD` vs `jubilee.940GZZLUSTD`,
 * both naptan `940GZZLUSTD`) collapse to a single signature variant.
 * Falls back to `stationId` when `naptanId` is absent.
 */
export function signature(r: Route): string[] {
  const out: string[] = []
  for (const leg of r.legs) {
    if (leg.type !== 'ride') continue
    for (const s of leg.stops ?? []) {
      const key = s.naptanId ?? s.stationId
      out.push(`${key}|${leg.line_id ?? ''}`)
    }
  }
  return out
}

/**
 * Returns the signature tokens joined by '>'.
 */
export function signatureKey(r: Route): string {
  return signature(r).join('>')
}

/**
 * Recomputes the route ID as the first 16 hex chars of SHA-1 over the
 * null-separated signature tokens.
 *
 * Uses an inline pure-JS SHA-1 so the function is synchronous AND
 * isomorphic — Node (vitest) and browser (Vite production build) take the
 * same path. Web Crypto (`crypto.subtle.digest`) would be ideal but is
 * async-only and would force `recomputeID` (and every caller —
 * `edgesToRoute`, `findRoutes`, `yenK`) to become Promise-typed; the
 * algorithm pipeline is internally synchronous and we don't want to
 * cascade `async` through it just for a 20-byte digest.
 *
 * The Node `node:crypto` import previously here was browser-incompatible:
 * Vite externalises it and Rolldown emits a stub whose shape mismatches
 * `createHash`, throwing `(0, ur.createHash) is not a function` at the
 * first algorithm call in the production bundle. The pure-JS path below
 * has no externals and is identical in behaviour (RFC 3174).
 */
export function recomputeID(r: Route): Route {
  const sigTokens = signature(r)
  // Build the same null-separated byte sequence the previous Node impl
  // hashed: each token's UTF-8 bytes followed by a 0x00 separator.
  const enc = new TextEncoder()
  const parts: Uint8Array[] = []
  let total = 0
  for (const tok of sigTokens) {
    const bytes = enc.encode(tok)
    parts.push(bytes)
    total += bytes.length + 1
  }
  const concat = new Uint8Array(total)
  let off = 0
  for (const p of parts) {
    concat.set(p, off)
    off += p.length
    // null separator
    concat[off] = 0
    off += 1
  }
  const digest = sha1Hex(concat).slice(0, 16)
  return { ...r, id: digest }
}

/**
 * Pure-JS SHA-1 over a `Uint8Array`. Returns the lowercase hex digest
 * (40 chars). Implementation per RFC 3174 — small enough to inline,
 * no external deps, isomorphic between Node and browser.
 *
 * NOTE: SHA-1 is used here ONLY as a stable identity hash for de-dup,
 * not as a security primitive. Cryptographic strength is not required.
 */
function sha1Hex(bytes: Uint8Array): string {
  // Pad message: append 0x80, then zeros, then 64-bit big-endian length
  // in bits. Final length must be ≡ 0 (mod 64).
  const bitLen = bytes.length * 8
  const withOne = bytes.length + 1
  const padLen = (64 - ((withOne + 8) % 64)) % 64
  const total = withOne + padLen + 8
  const msg = new Uint8Array(total)
  msg.set(bytes, 0)
  msg[bytes.length] = 0x80
  // 64-bit length: high 32 bits zero (we never hash >2^32 bits in this
  // app), low 32 bits big-endian.
  const lenLo = bitLen >>> 0
  msg[total - 4] = (lenLo >>> 24) & 0xff
  msg[total - 3] = (lenLo >>> 16) & 0xff
  msg[total - 2] = (lenLo >>> 8) & 0xff
  msg[total - 1] = lenLo & 0xff

  let h0 = 0x67452301
  let h1 = 0xefcdab89
  let h2 = 0x98badcfe
  let h3 = 0x10325476
  let h4 = 0xc3d2e1f0

  const w = new Int32Array(80)

  for (let chunk = 0; chunk < total; chunk += 64) {
    for (let i = 0; i < 16; i++) {
      const j = chunk + i * 4
      w[i] = (msg[j] << 24) | (msg[j + 1] << 16) | (msg[j + 2] << 8) | msg[j + 3] | 0
    }
    for (let i = 16; i < 80; i++) {
      const x = w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16]
      w[i] = (x << 1) | (x >>> 31) | 0
    }

    let a = h0
    let b = h1
    let c = h2
    let d = h3
    let e = h4

    for (let i = 0; i < 80; i++) {
      let f: number
      let k: number
      if (i < 20) {
        f = (b & c) | (~b & d)
        k = 0x5a827999
      } else if (i < 40) {
        f = b ^ c ^ d
        k = 0x6ed9eba1
      } else if (i < 60) {
        f = (b & c) | (b & d) | (c & d)
        k = 0x8f1bbcdc
      } else {
        f = b ^ c ^ d
        k = 0xca62c1d6
      }
      const temp = (((a << 5) | (a >>> 27)) + f + e + k + w[i]) | 0
      e = d
      d = c
      c = (b << 30) | (b >>> 2) | 0
      b = a
      a = temp
    }

    h0 = (h0 + a) | 0
    h1 = (h1 + b) | 0
    h2 = (h2 + c) | 0
    h3 = (h3 + d) | 0
    h4 = (h4 + e) | 0
  }

  return toHex8(h0) + toHex8(h1) + toHex8(h2) + toHex8(h3) + toHex8(h4)
}

function toHex8(n: number): string {
  return (n >>> 0).toString(16).padStart(8, '0')
}
